/**
 * WorkspaceUsage - observe-only LLM usage for workspace admins.
 * Settings > Workspaces > [workspace] > Usage. No billing, no limits.
 * Bet: docs/product/bets/2026-07-05-llm-usage-metering-ledger.md
 */

import { useCallback, useEffect, useState } from 'react'
import { BarChart3, Loader2, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  SettingsSection,
  SettingsSectionRow,
} from '@/components/features/settings/settings-section'
import { fetchWithAuth } from '@/lib/api'
import { testIds } from '@/lib/test-ids'

interface UsageTotals {
  requests: number
  prompt_tokens: number
  cache_read_tokens: number
  output_tokens: number
  cost_usd_micros: number | null
  unpriced_requests: number
  estimated_requests: number
}

interface UserUsage extends UsageTotals {
  actor_user_id: string | null
  email: string | null
  full_name: string | null
  byok_requests: number
}

interface ModelUsage extends UsageTotals {
  provider: string
  model: string | null
  compute_mode: string
}

interface UsageResponse {
  days: number
  users: UserUsage[]
  models: ModelUsage[]
}

const DAY_OPTIONS = [7, 30, 90]
const compact = new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 })

function formatCost(row: UsageTotals): string {
  const micros = row.cost_usd_micros ?? 0
  const priced = micros > 0 ? `$${(micros / 1_000_000).toFixed(2)}` : '$0.00'
  if (!row.unpriced_requests) return priced
  return row.unpriced_requests === row.requests ? 'Unpriced' : `${priced} + unpriced`
}

function UsageFigures({ row }: { row: UsageTotals }) {
  return (
    <div className="flex shrink-0 items-center gap-4 text-xs tabular-nums text-muted-foreground">
      <span title="Requests">{compact.format(row.requests)} req</span>
      <span title="Prompt tokens (incl. cached)">{compact.format(row.prompt_tokens)} in</span>
      <span title="Output tokens">{compact.format(row.output_tokens)} out</span>
      <span className="min-w-[5.5rem] text-right font-medium text-foreground" title="Estimated cost at list price">
        {formatCost(row)}
      </span>
    </div>
  )
}

export function WorkspaceUsage({ orgId, isAdmin }: { orgId: string; isAdmin: boolean }) {
  const [days, setDays] = useState(30)
  const [data, setData] = useState<UsageResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!isAdmin) return
    setIsLoading(true)
    setError(null)
    try {
      const res = await fetchWithAuth(`/api/organizations/${orgId}/llm-usage?days=${days}`)
      const body = await res.json()
      if (!res.ok || !body.success) throw new Error(body.error || `HTTP ${res.status}`)
      setData(body)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load usage')
    } finally {
      setIsLoading(false)
    }
  }, [orgId, days, isAdmin])

  useEffect(() => {
    void load()
  }, [load])

  if (!isAdmin) {
    return (
      <p className="text-sm text-muted-foreground" data-testid={testIds.workspaceSettings.sectionUsage}>
        Only workspace admins and owners can see usage.
      </p>
    )
  }

  return (
    <div className="space-y-6" data-testid={testIds.workspaceSettings.sectionUsage}>
      <SettingsSection
        title="LLM usage"
        icon={<BarChart3 />}
        description="Tokens this workspace paid for, by person. Observe-only - nothing is charged or limited. Costs are estimates at list price."
        actions={
          <div className="flex items-center gap-1">
            <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
              <SelectTrigger className="h-8 w-[7.5rem] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DAY_OPTIONS.map((d) => (
                  <SelectItem key={d} value={String(d)}>
                    Last {d} days
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => void load()} disabled={isLoading}>
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
          </div>
        }
      >
        {error ? <p className="text-sm text-red-500">{error}</p> : null}
        {!error && data && data.users.length === 0 ? (
          <p className="text-sm text-muted-foreground">No usage recorded in this window yet.</p>
        ) : null}
        <div className="space-y-2" data-testid={testIds.workspaceSettings.usageUserList}>
          {data?.users.map((u) => (
            <SettingsSectionRow key={u.actor_user_id ?? 'system'}>
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">
                    {u.full_name || u.email || (u.actor_user_id ? 'Unknown user' : 'Background jobs')}
                  </div>
                  {u.full_name && u.email ? (
                    <div className="truncate text-xs text-muted-foreground">{u.email}</div>
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  {u.byok_requests > 0 ? (
                    <Badge variant="outline" className="text-[10px]">Workspace key</Badge>
                  ) : null}
                  <UsageFigures row={u} />
                </div>
              </div>
            </SettingsSectionRow>
          ))}
        </div>
      </SettingsSection>

      {data && data.models.length > 0 ? (
        <SettingsSection title="By model" description="Top models in this window.">
          <div className="space-y-2">
            {data.models.map((m) => (
              <SettingsSectionRow key={`${m.provider}:${m.model}:${m.compute_mode}`}>
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0 truncate text-sm">
                    <span className="font-medium">{m.model || m.provider}</span>
                    <span className="ml-2 text-xs text-muted-foreground">
                      {m.provider} - {m.compute_mode === 'byok' ? 'workspace key' : m.compute_mode}
                    </span>
                  </div>
                  <UsageFigures row={m} />
                </div>
              </SettingsSectionRow>
            ))}
          </div>
        </SettingsSection>
      ) : null}
    </div>
  )
}
